import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

// Receipt formatting utilities
const operatorNameToCode: Record<string, string> = {
  'Magnum 4D': 'M',
  'Damacai 4D': 'P',
  'Sports Toto': 'T',
  'Singapore 4D': 'S',
  'Cash Sweep': 'B',
  'Sabah 88': 'K',
  'STC 4D': 'W',
  'Grand Dragon': 'H',
  '9 Lotto': 'L'
};

const operatorOrder = ['M', 'P', 'T', 'S', 'B', 'K', 'W', 'H', 'L'];

const formatNumber = (amount: number) => {
  if (amount === undefined || amount === null) return '';
  return Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
};

const formatDateDisplay = (input?: string) => {
  if (!input) return 'Unknown Date';
  if (/^\d{4}-\d{2}-\d{2}/.test(input)) {
    const [year, month, day] = input.split('-');
    return `${day}/${month}/${year}`;
  }
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return input;
  }
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const getPatternMultiplier = (pattern: string): number => {
  if (!pattern) return 1;
  const trimmed = pattern.trim();
  if (trimmed.includes('-')) {
    const [startStr, endStr] = trimmed.split('-');
    const start = Number(startStr);
    const end = Number(endStr);
    if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
      return Math.abs(end - start) + 1;
    }
  }
  return 1;
};

// Convert bet history detail to receipt format
const formatReceiptFromBetHistory = (group: BetSlipDetail): string => {
  const groups = new Map<string, {
    operators: Set<string>;
    numbers: Map<string, { number: string; amounts: string; isDrawNotOpen: boolean }>;
  }>();

  // Extract date
  let drawKey = 'unknown';
  if (group.drawDate) {
    if (typeof group.drawDate === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(group.drawDate)) {
        drawKey = group.drawDate.slice(0, 10);
      } else {
        const date = new Date(group.drawDate);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        drawKey = `${year}-${month}-${day}`;
      }
    }
  }

  if (!groups.has(drawKey)) {
    groups.set(drawKey, { operators: new Set(), numbers: new Map() });
  }

  const receiptGroup = groups.get(drawKey)!;

  // Add operators
  group.operators.forEach(op => {
    const operatorCode = operatorNameToCode[op.name] || op.name.charAt(0).toUpperCase();
    receiptGroup.operators.add(operatorCode);
  });

  // Build amounts string from betTypesWithAmounts
  const pieces: string[] = [];
  group.betTypesWithAmounts.forEach(bt => {
    if (bt.betType === 'Big' && bt.baseAmount > 0) pieces.push(`${formatNumber(bt.baseAmount)}B`);
    if (bt.betType === 'Small' && bt.baseAmount > 0) pieces.push(`${formatNumber(bt.baseAmount)}S`);
    if (bt.betType === '4A' && bt.baseAmount > 0) pieces.push(`${formatNumber(bt.baseAmount)}4A`);
    if (bt.betType === 'A' && bt.baseAmount > 0) pieces.push(`${formatNumber(bt.baseAmount)}A`);
    if (bt.betType === 'ABC' && bt.baseAmount > 0) pieces.push(`${formatNumber(bt.baseAmount)}ABC`);
    if (bt.betType === '5D' && bt.baseAmount > 0) pieces.push(`${formatNumber(bt.baseAmount)}5D`);
    if (bt.betType === '6D' && bt.baseAmount > 0) pieces.push(`${formatNumber(bt.baseAmount)}6D`);
  });

  const amounts = pieces.join(' ');
  const isDrawNotOpen = group.status === 'DRAW_NOT_OPEN' || group.totalAmount === 0;
  
  const multiplier = getPatternMultiplier(group.betNumber);
  let numberLabel = group.betNumber;
  if (multiplier > 1) {
    numberLabel = `${group.betNumber} (x${multiplier})`;
  }

  const lineContent = amounts ? `${numberLabel} ${amounts}` : numberLabel;
  const finalLine = isDrawNotOpen ? `${lineContent} (S.O)` : lineContent;

  receiptGroup.numbers.set(group.betNumber, {
    number: group.betNumber,
    amounts: finalLine,
    isDrawNotOpen
  });

  // Format receipt
  const displayDate = formatDateDisplay(drawKey);
  const operatorsSorted = Array.from(receiptGroup.operators).sort((a, b) => {
    const indexA = operatorOrder.indexOf(a);
    const indexB = operatorOrder.indexOf(b);
    const safeA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
    const safeB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
    return safeA - safeB;
  });

  const numberLines = Array.from(receiptGroup.numbers.values())
    .map(item => item.amounts);

  const blockLines = ['D', displayDate, operatorsSorted.join('') || '-'];
  blockLines.push(...numberLines);

  return blockLines.join('\n');
};

interface BetTypeSettlement {
  betType: string;
  stake: number;
  winAmount: number;
  isWin: boolean;
  winningTier: string;
  usedUserSpecificRules: boolean;
}

interface BetSlipDetail {
  betNumber: string;
  drawDate: string;
  optionType: string;
  // Settlement information
  isSettled: boolean;
  isWin: boolean;
  winAmount: number;
  winningTier: string;
  betTypeSettlements?: BetTypeSettlement[];
  operators: Array<{
    id: number;
    name: string;
  }>;
  betTypesWithAmounts: Array<{
    betType: string;
    baseAmount: number;
    totalAmount: number;
    optionType: string;
    combinationMultiplier?: number;
  }>;
  totalAmount: number;
  draw: {
    id: number;
    drawDate: string;
    gameId: number;
  };
  status?: string; // Added for DRAW_NOT_OPEN status
}

interface BetSlip {
  id: number;
  userId: number;
  totalCost: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
  canCancel: boolean;
  hasSettledBets: boolean;
  betSlipDetails: BetSlipDetail[];
}

interface GroupedBetSlip {
  id: number;
  userId: number;
  totalCost: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
  canCancel: boolean;
  hasSettledBets: boolean;
  betSlipDetails: BetSlipDetail[];
  earliestDrawDate: string;
}

const BetHistory: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [betHistory, setBetHistory] = useState<GroupedBetSlip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [betToCancel, setBetToCancel] = useState<GroupedBetSlip | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [allOperators, setAllOperators] = useState<string[]>([]);

  // Fetch all operators on initial load (without filters)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (isAuthenticated && token) {
      // Fetch all bet history once to populate operator list
      axios.get('/bet/history')
        .then(response => {
          const data: BetSlip[] = Array.isArray(response.data) ? response.data : (response.data?.bets || []);
          const operators = new Set<string>();
          data.forEach(betSlip => {
            betSlip.betSlipDetails?.forEach(detail => {
              detail.operators?.forEach(operator => {
                operators.add(operator.name);
              });
            });
          });
          setAllOperators(Array.from(operators).sort());
        })
        .catch(err => {
          console.error('Error fetching operators:', err);
        });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Debug: Check if user is authenticated and has token
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    console.log('=== BET HISTORY DEBUG ===');
    console.log('Authentication status:', isAuthenticated);
    console.log('Token in localStorage:', token ? `Present (${token.substring(0, 20)}...)` : 'Not found');
    console.log('Current user:', user ? JSON.parse(user) : 'No user data');
    console.log('========================');
    
    if (isAuthenticated && token) {
      console.log('✅ User authenticated, fetching bet history...');
      fetchBetHistory();
    } else {
      console.log('❌ User not authenticated or no token found.');
      console.log('🔑 Please login with: admin@lotterysystem.com / Admin123!');
      setLoading(false);
    }
  }, [isAuthenticated, startDate, endDate, selectedOperator]);

  const fetchBetHistory = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching bet history from API...');
      
      // Build query parameters
      const params: Record<string, string> = {};
      if (startDate) {
        params.startDate = format(startDate, 'yyyy-MM-dd');
      }
      if (endDate) {
        params.endDate = format(endDate, 'yyyy-MM-dd');
      }
      if (selectedOperator && selectedOperator !== 'all') {
        params.operatorName = selectedOperator;
      }
      
      const response = await axios.get('/bet/history', { params });
      
      console.log('📡 API Response received:', {
        status: response.status,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataKeys: response.data ? Object.keys(response.data) : 'null',
        dataLength: Array.isArray(response.data) ? response.data.length : 'not array',
        firstItem: Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null
      });
      
      // Handle the API response structure - it returns an object with bets array
      let data: BetSlip[];
      if (Array.isArray(response.data)) {
        // Direct array response (old format)
        data = response.data;
        console.log('📊 Using direct array format, found', data.length, 'bet slips');
      } else if (response.data && Array.isArray(response.data.bets)) {
        // Object with bets property (new format)
        data = response.data.bets;
        console.log('📊 Using object.bets format, found', data.length, 'bet slips');
      } else {
        console.error('❌ Unexpected API response format:', response.data);
        throw new Error('Invalid response format from bet history API');
      }
      
      // Process data to add cancellation logic
      const processedData = data.map(betSlip => {
        console.log('🔍 Processing bet slip:', betSlip.id, 'with', betSlip.betSlipDetails?.length || 0, 'details');
        console.log('📋 Bet slip details sample:', betSlip.betSlipDetails?.[0]);
        
        // Find the earliest draw date among all bet slip details
        // Handle both drawDate (direct) and draw.drawDate (nested) formats
        const validDrawDates = betSlip.betSlipDetails
          .map(detail => {
            // Try drawDate first (direct property), then draw.drawDate (nested)
            const drawDateStr = detail.drawDate || detail.draw?.drawDate;
            if (!drawDateStr) return null;
            const date = new Date(drawDateStr);
            return isNaN(date.getTime()) ? null : date;
          })
          .filter(date => date !== null) as Date[];
        
        let earliestDrawDate: Date;
        let canCancel = false;
        
        if (validDrawDates.length > 0) {
          earliestDrawDate = new Date(Math.min(...validDrawDates.map(date => date.getTime())));
          
          // Check if bet can be cancelled (before 7 PM UTC+8 on draw day)
          const nowUtc = new Date();
          
          // Create the cutoff time: 7 PM (19:00) on the earliest draw date in UTC+8
          // The earliestDrawDate is stored as UTC, so we need to:
          // 1. Get the date part of the earliest draw date in UTC+8
          // 2. Set the cutoff to 7 PM on that date in UTC+8
          // 3. Convert back to UTC for comparison
          const utcPlus8Offset = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
          
          // Convert earliest draw date to UTC+8 to get the local date
          const earliestDrawUtc8 = new Date(earliestDrawDate.getTime() + utcPlus8Offset);
          
          // Create the cutoff time: 7 PM on the earliest draw date in UTC+8
          // This creates a UTC timestamp for 7 PM on the draw date, then converts it back
          const cutoffUtc = new Date(Date.UTC(
            earliestDrawUtc8.getUTCFullYear(), 
            earliestDrawUtc8.getUTCMonth(), 
            earliestDrawUtc8.getUTCDate(), 
            19, 0, 0, 0
          ) - utcPlus8Offset); // Subtract UTC+8 offset to get UTC time

          canCancel = nowUtc < cutoffUtc && betSlip.status === 'CONFIRMED';
        } else {
          // Fallback to current date if no valid draw dates found
          earliestDrawDate = new Date();
        }
        
        // Format date as YYYY-MM-DD in local timezone
        const year = earliestDrawDate.getFullYear();
        const month = String(earliestDrawDate.getMonth() + 1).padStart(2, '0');
        const day = String(earliestDrawDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        
        return {
          ...betSlip,
          earliestDrawDate: formattedDate,
          canCancel
        };
      });
      
      setBetHistory(processedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBetClick = (betSlip: GroupedBetSlip) => {
    setBetToCancel(betSlip);
    setCancelDialogOpen(true);
  };

  const handleCancelBetConfirm = async () => {
    if (!betToCancel) return;
    
    try {
      await axios.post(`/bet/cancel/${betToCancel.id}`);
      // Refresh bet history after cancellation
      await fetchBetHistory();
      setCancelDialogOpen(false);
      setBetToCancel(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel bet');
    }
  };

  const handleCancelBetCancel = () => {
    setCancelDialogOpen(false);
    setBetToCancel(null);
  };

  // Use allOperators state for dropdown (populated from initial fetch)

  // Filter is now done server-side, but we keep this for display
  const getFilteredBetHistory = () => {
    return betHistory;
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedOperator('all');
  };


  if (!isAuthenticated) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 mb-4">Please log in to view your bet history.</p>
        <p className="text-sm text-blue-600">
          Test credentials: <strong>admin@lotterysystem.com</strong> / <strong>Admin123!</strong>
        </p>
        <a href="/login" className="text-blue-500 hover:text-blue-700 underline">
          Go to Login Page
        </a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600 font-medium">Loading your bet history...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8">
        <div className="max-w-md mx-auto">
          <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-red-400 font-bold text-lg">⚠</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading Bet History</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredBetHistory = getFilteredBetHistory();

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Bet History
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Track your betting activity and results
      </Typography>
      
      {/* Filters Section */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Date Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
            />
          </Box>
          
          {/* Operator Filter */}
          {allOperators.length > 0 && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="operator-filter-label">Filter by Operator</InputLabel>
              <Select
                labelId="operator-filter-label"
                value={selectedOperator}
                label="Filter by Operator"
                onChange={(e) => setSelectedOperator(e.target.value)}
                size="small"
              >
                <MenuItem value="all">All Operators</MenuItem>
                {allOperators.map((operator) => (
                  <MenuItem key={operator} value={operator}>
                    {operator}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {/* Clear Filters Button */}
          {(startDate || endDate || (selectedOperator && selectedOperator !== 'all')) && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={{ height: '40px' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </LocalizationProvider>
      
      {filteredBetHistory.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="text.secondary">
            No bet history found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You haven't placed any bets yet. Start betting to see your history here!
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mt: 3 }}>
          {filteredBetHistory.map((betSlip) => {
            // Backend now provides pre-grouped data
            const groupedDetails = betSlip.betSlipDetails || [];
            console.log('🎫 Bet Slip #' + betSlip.id + ' - Pre-grouped Details from Backend:', groupedDetails);
            console.log('🎫 Number of grouped details to render:', groupedDetails.length);
            console.log('🎫 Full bet slip data:', JSON.stringify(betSlip, null, 2));
            
            return (
              <Card key={betSlip.id} sx={{ 
                mb: 3,
                border: betSlip.status === 'CANCELLED' ? '2px solid #f44336' : '1px solid #e0e0e0',
                backgroundColor: betSlip.status === 'CANCELLED' ? '#ffebee' : 'white',
                opacity: betSlip.status === 'CANCELLED' ? 0.8 : 1
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Bet Slip #{betSlip.id}
                        {betSlip.status === 'CANCELLED' && (
                          <Chip 
                            label="CANCELLED" 
                            color="error" 
                            size="small" 
                            sx={{ ml: 1, fontSize: '0.7rem' }}
                          />
                        )}
                        {betSlip.hasSettledBets && (
                          <Chip 
                            label="SETTLED" 
                            color="success" 
                            size="small" 
                            sx={{ ml: 1, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {betSlip.createdAt ? (
                          isNaN(new Date(betSlip.createdAt).getTime()) ? 
                            'Invalid Date' : 
                            format(new Date(betSlip.createdAt), 'MMM dd, yyyy HH:mm')
                        ) : 'No Date'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h5" color="primary" fontWeight="bold">
                        ${betSlip.totalCost.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Cost
                      </Typography>
                      {betSlip.hasSettledBets && (
                        <>
                          <Typography variant="h6" color="success.main" fontWeight="bold" sx={{ mt: 1 }}>
                            ${betSlip.betSlipDetails
                              .filter(detail => detail.isWin)
                              .reduce((sum, detail) => sum + detail.winAmount, 0)
                              .toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            Total Win
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Earliest Draw: {betSlip.earliestDrawDate ? (
                        isNaN(new Date(betSlip.earliestDrawDate).getTime()) ? 
                          'Invalid Date' : 
                          format(new Date(betSlip.earliestDrawDate), 'MMM dd, yyyy')
                      ) : 'No Date'}
                    </Typography>
                      <Chip
                        label={betSlip.status}
                        color={
                          betSlip.status === 'CANCELLED' ? 'error' :
                          betSlip.status === 'CONFIRMED' ? 'success' :
                          betSlip.status === 'PENDING' ? 'warning' :
                          'default'
                        }
                        variant="filled"
                        size="small"
                      />
                    </Box>
                    {betSlip.canCancel ? (
                      <Chip
                        label="Cancel Bet"
                        color="error"
                        variant="filled"
                        onClick={() => handleCancelBetClick(betSlip)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : betSlip.hasSettledBets ? (
                      <Chip
                        label="Settled"
                        color="success"
                        variant="outlined"
                        sx={{ cursor: 'not-allowed' }}
                      />
                    ) : (
                      <Chip
                        label="Cancel Unavailable"
                        color="default"
                        variant="outlined"
                        sx={{ cursor: 'not-allowed' }}
                      />
                    )}
                  </Box>
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Numbers</TableCell>
                          <TableCell>Draw Date</TableCell>
                          <TableCell>Bet Types</TableCell>
                          <TableCell>Option Type</TableCell>
                          <TableCell>Operators</TableCell>
                          <TableCell>Result</TableCell>
                          <TableCell align="right">Stake</TableCell>
                          <TableCell align="right">Win Amount</TableCell>
                          <TableCell>Receipt</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedDetails && groupedDetails.length > 0 ? (
                          groupedDetails.map((group, index) => {
                            console.log('📊 Rendering group', index, ':', group);
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                    {group.betNumber !== undefined && group.betNumber !== null ? group.betNumber : '---'}
                                  </Typography>
                                </TableCell>
                            <TableCell>
                              <Chip
                                label={group.drawDate ? (
                                  isNaN(new Date(group.drawDate).getTime()) ? 
                                    'Invalid Date' : 
                                    format(new Date(group.drawDate), 'MMM dd, yyyy')
                                ) : 'No Date'}
                                color="success"
                                variant="outlined"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {group.betTypesWithAmounts.map((betTypeData, btIndex: number) => {
                                  const combinationMultiplier = betTypeData.combinationMultiplier ?? 1;
                                  const label = combinationMultiplier > 1
                                    ? `${betTypeData.betType}: $${betTypeData.baseAmount.toFixed(2)} (x${combinationMultiplier})`
                                    : `${betTypeData.betType}: $${betTypeData.baseAmount.toFixed(2)}`;

                                  return (
                                    <Chip
                                      key={btIndex}
                                      label={label}
                                      color="warning"
                                      variant="outlined"
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {(() => {
                                  const optionTypes = group.betTypesWithAmounts
                                    .map((betTypeData: any) => betTypeData.optionType)
                                    .filter((value: string | undefined) => !!value && value.trim().length > 0);

                                  if (group.optionType && optionTypes.length === 0) {
                                    optionTypes.push(group.optionType);
                                  }

                                  const uniqueOptionTypes = Array.from(new Set(optionTypes));

                                  const optionTypeLabels: Record<string, string> = {
                                    single: 'Single',
                                    box: 'Box',
                                    reverse: 'Reverse',
                                    range: 'Range',
                                    rollprefix: 'Roll (Prefix)',
                                    rollsuffix: 'Roll (Suffix)',
                                    ibox: 'iBox',
                                    rollfirstdigit: 'Roll 1st Digit',
                                    rollseconddigit: 'Roll 2nd Digit',
                                    rollthirddigit: 'Roll 3rd Digit',
                                    rollfourthdigit: 'Roll 4th Digit'
                                  };

                                  return uniqueOptionTypes.map((optionType, btIndex) => {
                                    const normalized = optionType.toLowerCase();
                                    const label = optionTypeLabels[normalized] ?? optionType;
                                    return (
                                      <Chip
                                        key={btIndex}
                                        label={label}
                                        color="secondary"
                                        variant="filled"
                                        size="small"
                                      />
                                    );
                                  });
                                })()}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {group.operators.map((operator, opIndex: number) => (
                                  <Chip
                                    key={opIndex}
                                    label={operator.name}
                                    color="info"
                                    variant="outlined"
                                    size="small"
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {group.status === 'DRAW_NOT_OPEN' ? (
                                <Chip
                                  label="Draw Not Open"
                                  color="warning"
                                  variant="filled"
                                  size="small"
                                />
                              ) : group.isSettled ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {group.betTypeSettlements && group.betTypeSettlements.length > 0 ? (
                                    group.betTypeSettlements.map((settlement, idx) => (
                                      <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Chip
                                          label={settlement.isWin ? `${settlement.betType} WON` : `${settlement.betType} LOST`}
                                          color={settlement.isWin ? 'success' : 'error'}
                                          variant="filled"
                                          size="small"
                                        />
                                        {settlement.isWin && (
                                          <Typography variant="caption" color="success.main" fontWeight="bold">
                                            ${settlement.winAmount.toFixed(2)}
                                            {settlement.usedUserSpecificRules && (
                                              <span style={{ fontSize: '0.7em', color: '#1976d2', marginLeft: '4px' }}>
                                                (Custom Rules)
                                              </span>
                                            )}
                                          </Typography>
                                        )}
                                      </Box>
                                    ))
                                  ) : (
                                    <Chip
                                      label={group.isWin ? `WON - ${group.winningTier}` : 'LOST'}
                                      color={group.isWin ? 'success' : 'error'}
                                      variant="filled"
                                      size="small"
                                    />
                                  )}
                                  {group.isWin && group.betTypeSettlements && group.betTypeSettlements.length === 0 && (
                                    <Typography variant="body2" color="success.main" fontWeight="bold">
                                      ${group.winAmount.toFixed(2)}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Chip
                                  label="Pending"
                                  color="warning"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" color="primary.main">
                                ${group.totalAmount.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {group.status === 'DRAW_NOT_OPEN' ? (
                                <Typography variant="h6" color="text.secondary">
                                  N/A
                                </Typography>
                              ) : group.isSettled && group.isWin ? (
                                <Typography variant="h6" color="success.main" fontWeight="bold">
                                  ${group.winAmount.toFixed(2)}
                                </Typography>
                              ) : group.isSettled && !group.isWin ? (
                                <Typography variant="h6" color="text.secondary">
                                  $0.00
                                </Typography>
                              ) : (
                                <Typography variant="h6" color="warning.main">
                                  Pending
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                component="pre"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  whiteSpace: 'pre-wrap',
                                  maxWidth: '300px',
                                  overflow: 'auto',
                                  p: 1,
                                  backgroundColor: 'background.default',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  maxHeight: '150px'
                                }}
                              >
                                {formatReceiptFromBetHistory(group)}
                              </Box>
                            </TableCell>
                          </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No bet details found for this bet slip
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Cancel Bet Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCancelBetCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Bet Confirmation</DialogTitle>
        <DialogContent>
          {betToCancel && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Bet Information:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Amount" 
                    secondary={`RM ${betToCancel.totalCost.toFixed(2)}`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Draw Date" 
                    secondary={
                      betToCancel.earliestDrawDate && 
                      !isNaN(new Date(betToCancel.earliestDrawDate).getTime()) ? 
                        format(new Date(betToCancel.earliestDrawDate), 'MMM dd, yyyy') : 
                        'No Date'
                    } 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Bet Types & Options" 
                    secondary={betToCancel.betSlipDetails.flatMap(detail => 
                      detail.betTypesWithAmounts.map(bt => `${bt.betType} (${bt.optionType})`)
                    ).join(', ')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Numbers" 
                    secondary={betToCancel.betSlipDetails.map(detail => detail.betNumber).join(', ')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Status" 
                    secondary={betToCancel.status} 
                  />
                </ListItem>
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                Are you sure you want to cancel this bet?
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelBetCancel} variant="outlined">
            No, Keep
          </Button>
          <Button onClick={handleCancelBetConfirm} variant="contained" color="error">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BetHistory;